import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Info, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { getAttribution, trackEvent } from "@/lib/tracking";
import { useSettings, useSeo } from "@/context/SettingsContext";
import { Btn } from "@/components/common";
import { SECTIONS, FieldInput, validateFields, splitValues, useFormConfig } from "@/components/DynamicFields";
import { EmbeddedCardStep } from "@/components/site/EmbeddedCard";

const Section = ({ n, title, children }) => (
  <fieldset className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
    <legend className="sr-only">{title}</legend>
    <div className="mb-6 flex items-center gap-3"><span className="grid h-8 w-8 place-items-center rounded-full bg-emerald-700 text-sm font-bold text-white">{n}</span><h2 className="font-display text-xl font-bold text-slate-900">{title}</h2></div>
    <div className="grid gap-5 sm:grid-cols-2">{children}</div>
  </fieldset>
);

function Aside({ fee }) {
  return (
    <aside className="lg:col-span-4">
      <div className="lg:sticky lg:top-28">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Book your trial</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">Request your {money(fee).replace(".00", "")} home trial</h1>
        <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="booking-request-disclaimer">
          <p className="flex items-center gap-2 font-display font-bold text-emerald-900"><Info className="h-5 w-5" />This is a request, not a confirmed booking</p>
          <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-emerald-900/90">
            <li>No payment is taken when you submit this form.</li>
            <li>We'll contact you to confirm eligibility, mower availability and delivery or pickup.</li>
            <li>A card is required to hold every trial. It's saved securely by Stripe and the {money(fee)} trial fee is charged only after your trial is confirmed.</li>
          </ul>
        </div>
        <p className="mt-6 flex items-start gap-2 text-sm text-slate-600"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />Your details are kept confidential and used only to manage your trial request.</p>
      </div>
    </aside>
  );
}

export default function Book() {
  const { settings } = useSettings();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const fields = useFormConfig("booking");
  const [vals, setVals] = useState({ preferred_product_id: params.get("product") || "" });
  const [consent, setConsent] = useState({ terms: false, marketing: false });
  const [hp, setHp] = useState("");
  const [errors, setErrors] = useState({});
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cardStep, setCardStep] = useState(null);
  const started = useRef(Date.now());
  useSeo("Book Your $1 Trial", "Request a $1 at-home lawn mower trial. Submitting is a request — no payment is taken.");
  useEffect(() => { api.get("/public/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  const ctx = { products, delivery: settings.delivery_options || [] };
  const shown = (fields || []).filter((f) => !(f.dynamic === "delivery" && !ctx.delivery.length));

  const setVal = (k) => (v) => { setVals((s) => ({ ...s, [k]: v })); if (errors[k]) setErrors((x) => ({ ...x, [k]: undefined })); };
  const submit = async (e) => {
    e.preventDefault();
    const errs = validateFields(shown, vals);
    if (!consent.terms) errs.consent_terms = "You must accept the Privacy Policy and Trial Terms to continue";
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Please fix the highlighted fields"); document.querySelector(`[name="${Object.keys(errs)[0]}"]`)?.focus(); return; }
    setLoading(true);
    try {
      const r = await api.post("/public/bookings", { ...splitValues(shown, vals), consent_terms: consent.terms, consent_marketing: consent.marketing, company_website: hp, attribution: getAttribution(), form_elapsed_ms: Date.now() - started.current });
      trackEvent("form_submission", { value: Number(settings.trial_fee), currency: "USD" });
      try {
        const embedded = !!settings.stripe_publishable_key;
        const s = await api.post(`/public/bookings/${r.data.reference}/save-card`, { origin_url: window.location.origin, embedded });
        if (s.data.client_secret) { setCardStep({ ref: r.data.reference, secret: s.data.client_secret }); window.scrollTo(0, 0); return; }
        window.location.href = s.data.url;
        return;
      } catch (err) {
        if (err.response?.status === 409) { nav(`/book/confirmation/${r.data.reference}`, { replace: true }); return; }
        toast.error(`Card step unavailable: ${errMsg(err)}`);
        nav(`/book/confirmation/${r.data.reference}?card=cancelled`, { replace: true });
      }
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  const sections = SECTIONS.booking.map(([k, t]) => [t, shown.filter((f) => (SECTIONS.booking.some(([s]) => s === f.section) ? f.section : "extra") === k)]).filter(([, l]) => l.length);

  return (
    <div className="bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12">
          <Aside fee={settings.trial_fee} />
          {cardStep && <EmbeddedCardStep publishableKey={settings.stripe_publishable_key} clientSecret={cardStep.secret} reference={cardStep.ref} fee={settings.trial_fee} />}
          {!cardStep && <form onSubmit={submit} noValidate className="space-y-6 lg:col-span-8" data-testid="trial-booking-form">
            <input type="text" name="company_website" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" />
            {!fields && <p className="text-slate-500">Loading form…</p>}
            {sections.map(([title, list], i) => (
              <Section key={title} n={i + 1} title={title}>
                {list.map((f) => <FieldInput key={f.key} f={f} value={vals[f.key]} onChange={setVal(f.key)} error={errors[f.key]} ctx={ctx} testPrefix="trial-booking" />)}
              </Section>
            ))}
            {fields && (
              <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                <label className="flex items-start gap-3">
                  <input type="checkbox" checked={consent.terms} onChange={(e) => { setConsent({ ...consent, terms: e.target.checked }); setErrors((x) => ({ ...x, consent_terms: undefined })); }} data-testid="trial-booking-consent-terms-checkbox" className="mt-1 h-5 w-5 shrink-0 accent-emerald-700" />
                  <span className="text-sm leading-relaxed text-slate-700">I have read and agree to the <Link to="/privacy-policy" target="_blank" className="font-semibold text-emerald-800 underline">Privacy Policy</Link>, <Link to="/terms" target="_blank" className="font-semibold text-emerald-800 underline">Terms and Conditions</Link> and <Link to="/trial-return-policy" target="_blank" className="font-semibold text-emerald-800 underline">Trial and Return Policy</Link>, and consent to being contacted about this request. <span className="text-emerald-700">*</span></span>
                </label>
                {errors.consent_terms && <p className="text-xs font-medium text-rose-600" role="alert" data-testid="consent-terms-error">{errors.consent_terms}</p>}
                <label className="flex items-start gap-3">
                  <input type="checkbox" checked={consent.marketing} onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })} data-testid="trial-booking-consent-marketing-checkbox" className="mt-1 h-5 w-5 shrink-0 accent-emerald-700" />
                  <span className="text-sm leading-relaxed text-slate-700">Optional: send me occasional offers and lawn-care tips. You can unsubscribe at any time.</span>
                </label>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="card-required-notice">
                  <p className="text-sm leading-relaxed text-slate-800"><b>Card required for every trial.</b> After you submit, you'll add a card on Stripe's secure page to hold your trial. Nothing is charged today — the {money(settings.trial_fee)} trial fee is charged only after our team confirms your trial.</p>
                  <p className="mt-2 flex items-start gap-2 text-xs leading-relaxed text-slate-600"><Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700" />Your card number and CVV go directly to Stripe and are never seen or stored by us. By saving a card you authorize one charge of {money(settings.trial_fee)} after confirmation; any other charge (such as a purchase) will only be made with your separate agreement.</p>
                </div>
                <Btn type="submit" size="lg" loading={loading} className="w-full sm:w-auto" data-testid="trial-booking-submit-button">Continue to Secure Card Details</Btn>
                <p className="text-xs text-slate-500">Submitting does not charge you or confirm a booking.</p>
              </div>
            )}
          </form>}
        </div>
      </div>
    </div>
  );
}
